import { useEffect, useState, type ReactNode } from "react";
import * as ml from "../../api/ml";
import { Icon } from "../../ui/icons";
import { RangeSlider } from "../../ui/RangeSlider";
import {
  CHECK_OPTIONS, PRESET_LABEL, PRESET_ORDER, RANGE_OPTIONS, STORED_PRESETS, optionsOn, presetOf, rangeOf, rangeOn, rangeText, signed,
  storedPresetOf, withRange,
} from "../../lib/augment";

/**
 * The Augment page's settings: a preset and, behind it, one checkbox per option. A ticked option applies to every
 * copy; rotation, brightness, contrast, zoom and hue also take a range, and each copy draws one value inside it.
 */
export function AugmentSettings({ value, onChange }: { value: ml.AugmentConfig; onChange: (v: ml.AugmentConfig) => void }) {
  const preset = storedPresetOf(value);
  return (
    <>
      <div className="row" style={{ flexWrap: "nowrap", marginBottom: 6 }}>
        <span style={{ whiteSpace: "nowrap" }}>Augmentation:</span>
        <select className="select" value={preset} data-testid="augment-preset"
          onChange={(e) => { const p = STORED_PRESETS[e.target.value]; if (p) onChange({ ...p }); }}>
          {PRESET_ORDER.map((n) => <option key={n} value={n}>{PRESET_LABEL[n]}</option>)}
          {preset === "custom" && <option value="custom">{PRESET_LABEL.custom}</option>}
        </select>
      </div>
      <details className="guide" data-testid="augment-settings" open>
        <summary>Settings ({optionsOn(value)} on)</summary>
        <div className="augment-opts">
          {CHECK_OPTIONS.map((o) => (
            <label key={o.key} className="opt-label" title={o.help}>
              <input type="checkbox" checked={value[o.key] > 0} data-testid={`augment-on-${o.key}`}
                onChange={(e) => onChange({ ...value, [o.key]: e.target.checked ? 1 : 0 })} />
              <span>{o.label}</span>
              <span /><span className="muted opt-value">{value[o.key] > 0 ? "every copy" : "off"}</span>
            </label>
          ))}
          {RANGE_OPTIONS.map((o) => {
            const on = rangeOn(value, o);
            const [lo, hi] = rangeOf(value, o);
            return (
              <label key={o.id} className="opt-label" title={o.help}>
                <input type="checkbox" checked={on} data-testid={`augment-on-${o.id}`}
                  onChange={(e) => onChange(e.target.checked ? withRange(value, o, o.start[0], o.start[1]) : withRange(value, o, 0, 0))} />
                <span>{o.label}</span>
                <RangeSlider min={o.min} max={o.max} step={o.step} low={lo} high={hi} disabled={!on} label={o.label} testId={`augment-${o.id}`}
                  lowLabel={o.low ? `${signed(o.min, o.unit)} ${o.low}` : signed(o.min, o.unit)} highLabel={o.high ? `${signed(o.max, o.unit)} ${o.high}` : signed(o.max, o.unit)}
                  onChange={(a, b) => onChange(withRange(value, o, a, b))} />
                <span className="muted opt-value" data-testid={`augment-${o.id}-text`}>{on ? rangeText(o, lo, hi) : "off"}</span>
              </label>
            );
          })}
        </div>
        {RANGE_OPTIONS.filter((o) => o.warning && rangeOn(value, o)).map((o) => (
          <div key={o.id} className="warning-text" style={{ marginTop: 6 }} data-testid={`augment-${o.id}-warning`}>{o.warning}</div>
        ))}
        <div className="muted" style={{ marginTop: 6 }}>
          Validation images are never changed. A ticked option is applied to every copy; for the ranges, each copy gets a
          random value between the two ends.
        </div>
      </details>
    </>
  );
}

/** "Preview augmentation": six augmented versions of one reviewed image, rendered by the server and kept nowhere. */
export function AugmentPreview({ projectId, value, imgSize, canPreview, note }: {
  projectId: number; value: ml.AugmentConfig; imgSize: number; canPreview: boolean; note: ReactNode;
}) {
  const [preview, setPreview] = useState<ml.AugmentPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState("");
  const show = async (imageId: number | null) => {
    setPreviewing(true); setError("");
    try { setPreview(await ml.augmentPreview(projectId, { augmentation: value, image_id: imageId, count: 6, img_size: Math.min(imgSize, 320) })); }
    catch (e) { setError((e as Error).message); }
    finally { setPreviewing(false); }
  };
  return (
    <>
      <div className="row" style={{ marginTop: 8 }}>
        <button className="btn" disabled={!canPreview || previewing} onClick={() => show(preview?.image_id ?? null)} data-testid="augment-preview"
          title={canPreview ? "See six augmented versions of one of your reviewed images with these settings." : "Review some annotations first: the preview uses your own images."}>
          {previewing ? "Rendering…" : <><Icon name="eye" />Preview</>}</button>
        {preview && <button className="btn" disabled={previewing} onClick={() => show(null)} data-testid="augment-another"><Icon name="refresh" />Another image</button>}
      </div>
      {error && <div className="error-text" data-testid="augment-error">{error}</div>}
      {preview && (
        <>
          <div className="augment-grid" data-testid="augment-grid">
            <figure><img src={preview.original} alt={`${preview.filename}, as training sees it without augmentation`} /><figcaption>Original — {preview.filename}</figcaption></figure>
            {preview.samples.map((src, i) => (
              <figure key={i}><img src={src} alt={`Augmented version ${i + 1}`} /><figcaption>Version {i + 1}</figcaption></figure>
            ))}
          </div>
          {/* People looked for a save or apply step here: say what happens to these pictures. */}
          <div className="muted" style={{ marginTop: 6 }} data-testid="augment-preview-note">{note}</div>
        </>
      )}
    </>
  );
}

/** One line per preset, for the Train page's Advanced section (presets: insiso_core/augment.py PRESETS). */
export const PRESET_HELP: Record<string, string> = {
  off: "No changes: the model sees your images exactly as they are.",
  light: "Mirrored copies and small lighting changes, for scenes that always look the same.",
  default: "What training has always used. Right for almost every project.",
  heavy: "Turned, flipped, re-lit, smaller, blurred and noisy versions, for few images or very varied scenes.",
  custom: "Settings changed by hand.",
};

/**
 * The Train page's augmentation control: a preset only, inside Advanced. Training makes varied copies of each
 * training image on the fly, so there is nothing to preview or save here; the sliders and the preview live in the
 * Augment page, where copies really are made and kept. Web only (docs/DESKTOP_PORT_BACKLOG.md).
 */
export function AugmentPanel({ projectId, value, onChange }: {
  projectId: number; value: ml.AugmentConfig; onChange: (v: ml.AugmentConfig) => void;
}) {
  const [presets, setPresets] = useState<Record<string, ml.AugmentConfig>>({});
  useEffect(() => { ml.augmentPresets(projectId).then(setPresets).catch(() => {}); }, [projectId]);
  const preset = presetOf(value, presets);
  return (
    <div data-testid="augment">
      <div className="row" style={{ flexWrap: "nowrap", marginBottom: 4 }}>
        <span style={{ whiteSpace: "nowrap" }}>Augmentation:</span>
        <select className="select" value={preset} data-testid="augment-preset"
          onChange={(e) => { const p = presets[e.target.value]; if (p) onChange(p); }}>
          {PRESET_ORDER.filter((n) => presets[n]).map((n) => <option key={n} value={n}>{PRESET_LABEL[n]}</option>)}
          {preset === "custom" && <option value="custom">{PRESET_LABEL.custom}</option>}
        </select>
      </div>
      <div className="muted" data-testid="augment-help">{PRESET_HELP[preset] ?? ""} Validation images are never changed.</div>
    </div>
  );
}
